import { Box } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "../../../localization/useTranslation.ts";
import {
    AutoRenewalValue,
    ContractFieldValue,
    ContractStatusWithOverdue,
    ContractTemplateInfo,
    RenewalCountdownRange,
    RenewalCountdownValue,
} from "../../../contract/Contracts.types.ts";
import { AmountWithCurrency, DateWithOptionalTimeValue, EntityFieldType, EvaluationValue, NameWithColor, NoDateReason } from "@common/entityField.types";
import { AutoRenewalDurationType } from "../../../contract/contract/Contract.types.ts";
import { DotLegalCompletedPercentage, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import MentionInputRender from "../mentionInput/MentionInputRender.tsx";
import TruncatedMentionText, { multiLineTextStyles } from "@common/components/mentionInput/TruncatedMentionText";
import ContractStatusChip from "../../../contract/contractOverview/ContractStatusChip.tsx";
import LegalEntitiesColumn from "../tableColumn/LegalEntitiesColumn.tsx";
import { isNullOrWhitespace, setFirstLetterToLowerCase } from "../../stringOperations.ts";
import { ContractColumn } from "../../../contract/contractDisplayFields/ContractDisplayFields.types.ts";
import { SystemFieldValue } from "../../../system/systems/Systems.types.ts";

import RiskIndicator from "../../../riskAssessment/riskIndicator/RiskIndicator.tsx";
import { RiskAssessmentType } from "../../../riskAssessment/RiskAssessment.types.ts";
import { RiskAssessmentArea, RiskComplianceArea } from "../../../riskAssessment/riskAssessments/RiskAssessments.types.ts";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import {
    OperationCriticalityLevelEnum,
    SystemDataType,
    SystemEntityType,
    SystemStatus,
    SystemStorageType,
} from "../../../system/systemEdit/SystemEdit.types.ts";
import { exhaustiveGuard } from "@common/utilities.ts";
import { DistributionArea } from "../../../processingActivity/systemsStep/SystemsStep.types.ts";
import SystemStatusChip from "../../../system/systemOverview/SystemStatusChip.tsx";
import { SystemColumn } from "../../../system/systems/systemDisplayFields/SystemDisplayFields.types.ts";
import SystemIcon from "../../../system/systemIcon/SystemIcon.tsx";
import { EntityType } from "@common/Common.types.ts";
import RiskAreaHighestRiskLine from "../../../risk/riskAreaRisksTile/RiskAreaHighestRiskLine.tsx";
import ColoredChipsCell, { ColoredChips } from "@common/components/coloredChipsCell/ColoredChipsCell";
import { RiskAreaHighestRisk } from "../../../risk/riskAreaRisksTile/RiskAreaRisks.types.ts";
import { LegalEntityAuditStatusValue, LegalEntityFieldValue, LegalEntityScope } from "../../../legalEntity/legalEntities/LegalEntities.types.ts";
import { IncidentLogFieldValue } from "../../../incidentLog/incidentLogs/IncidentLogs.types.ts";
import { LegalEntityClassificationInformationViewModel } from "../../../legalEntityAuditGroup/legalEntityAuditGroupDraftOverview/auditGroupLegalEntitySelector/AuditGroupLegalEntitySelector.types.ts";
import ClassificationRisk from "../questionnaireComponents/classiificationRisk/ClassificationRisk.tsx";
import LegalEntityStatusChip from "../../../legalEntity/legalEntities/LegalEntityStatusChip.tsx";
import QuestionnaireStatus from "../questionnaireComponents/questionnaireStatus/QuestionnaireStatus.tsx";
import QuestionnaireEvaluation from "@common/components/questionnaireComponents/questionnaireEvaluation/QuestionnaireEvaluation";
import IncidentLogStatusChip from "../../../incidentLog/incidentLogs/IncidentLogStatusChip.tsx";
import IncidentLogRisk from "@common/components/incidentLogRisk/IncidentLogRisk.tsx";
import { IncidentLogEntryStatus, SeverityLevelEnum } from "../../../incidentLog/IncidentLogDialog.types.ts";
import {
    ProcessingActivityFieldValue,
    ProcessingActivityProgressValue,
    ProcessingActivityStatus,
} from "../../../processingActivity/processingActivities/ProcessingActivities.types.ts";
import ProcessingActivityStatusChip from "../../../processingActivity/processingActivities/ProcessingActivityStatusChip.tsx";
import { ProcessFieldValue, ProcessStatus } from "../../../process/Process.types.ts";
import ProcessStatusChip from "../../../process/ProcessStatusChip.tsx";
import { SharingFieldValue } from "../../../sharings/Sharings.types.ts";
import ComplianceAreaWrapper from "@common/components/complianceAreaWrapper/ComplianceAreaWrapper";

export interface DynamicCellValueRendererProps {
    field:
        | ContractFieldValue
        | SystemFieldValue
        | LegalEntityFieldValue
        | IncidentLogFieldValue
        | ProcessingActivityFieldValue
        | ProcessFieldValue
        | SharingFieldValue;
    entityId?: string;
    showFullContent?: boolean;
    // Which kind of entity the row is, for the risk area links. Systems were the only rows with a Risk column
    // when this was written, hence the default.
    riskEntityType?: EntityType;
    // Only a legal entity row has one; the classification cell links into that page.
    legalEntityScope?: LegalEntityScope;
}

// Only systems and processing activities have a question score, so every other row keeps the Systems area.
function getRiskAssessmentArea(riskEntityType: EntityType): RiskAssessmentArea {
    return riskEntityType === EntityType.ProcessingActivity ? RiskAssessmentArea.ProcessingActivities : RiskAssessmentArea.Systems;
}

function DynamicCellValueRenderer({ field, entityId, showFullContent, riskEntityType = EntityType.System, legalEntityScope }: DynamicCellValueRendererProps) {
    const {
        translateString,
        translateDate,
        translateDateWithTime,
        translateContractStatusEnum,
        translateCurrencyNames,
        translateNumber,
        translateOperationCriticalityLevelEnum,
        translateCountry,
    } = useTranslation();
    const { getRiskAssessmentQuestionsDialog } = useUrlProvider();

    // Parsed without a zone suffix so the browser reads it as local time, the same way the Date cells read the
    // backend's unzoned DateTime values.
    function renderDateWithOptionalTime(value: DateWithOptionalTimeValue) {
        if (value.noDateReason) {
            return translateString(setFirstLetterToLowerCase(NoDateReason[value.noDateReason]));
        }

        if (value.time) {
            return translateDateWithTime(new Date(`${value.date}T${value.time}`));
        }

        return translateDate(new Date(`${value.date}T00:00:00`));
    }

    function renderFieldValue() {
        if (field.type === EntityFieldType.RiskScore) {
            const riskData = field.value as { score: number | undefined; versionId: string | undefined } | null;
            return (
                <RiskIndicator
                    riskAssessmentType={RiskAssessmentType.None}
                    riskScoreTextColor="darkblue"
                    removeLeftMargin
                    score={riskData?.score}
                    hideRiskType
                    showHighRiskWarning
                    linkHref={
                        entityId
                            ? getRiskAssessmentQuestionsDialog(
                                  entityId,
                                  getRiskAssessmentArea(riskEntityType),
                                  RiskComplianceArea.GDPR,
                                  riskData?.versionId ?? "null"
                              )
                            : undefined
                    }
                />
            );
        }

        if (field.type === EntityFieldType.SystemIcon) {
            return <SystemIcon systemName={field.value as string | undefined} size="small" />;
        }

        if (field.type === EntityFieldType.RiskAreas) {
            const riskAreas = (field.value as Array<RiskAreaHighestRisk> | null) ?? [];
            if (riskAreas.length === 0 || !entityId) return "";

            return <RiskAreaHighestRiskLine riskAreas={riskAreas} entityType={riskEntityType} entityId={entityId} />;
        }

        if (field.type === EntityFieldType.LegalEntityClassifications) {
            const classifications = (field.value as Array<LegalEntityClassificationInformationViewModel> | null) ?? [];
            if (classifications.length === 0 || !entityId || legalEntityScope === undefined) return "";

            return <ClassificationRisk legalEntityId={entityId} legalEntityScope={legalEntityScope} classifications={classifications} />;
        }

        if (field.type === EntityFieldType.LegalEntityAuditStatus) {
            const audit = field.value as LegalEntityAuditStatusValue | null;
            if (!audit) return "";

            return (
                <QuestionnaireStatus
                    showQuestionsStatistics={audit.showQuestionsStatistics}
                    numberOfQuestionsAnswered={audit.numberOfQuestionsAnswered}
                    totalNumberOfQuestions={audit.totalNumberOfQuestions}
                    status={audit.status}
                    lastestRecipientUpdate={audit.lastestRecipientUpdate || undefined}
                />
            );
        }

        if (field.value === null || field.value === undefined) {
            return "";
        }

        switch (field.type) {
            case EntityFieldType.CurrencyCode: {
                if (isNullOrWhitespace(field.value as string)) {
                    return "";
                }

                return translateCurrencyNames(field.value);
            }

            case EntityFieldType.Text:
            case EntityFieldType.Guid:
                if ((field as SystemFieldValue).column === SystemColumn.DataProcessorAgreement) {
                    if (field.value === "true") return translateString("yes");
                    if (field.value === "false") return translateString("no");
                }
                return field.value;

            // The line breaks are the layout, so the cell keeps every one of them instead of ellipsising onto a
            // single line the way MultiLineText does.
            case EntityFieldType.Contacts: {
                return (
                    <Box component={"span"} sx={{ whiteSpace: "pre" }}>
                        {field.value as string}
                    </Box>
                );
            }

            case EntityFieldType.Html: {
                const text = field.value as string;

                if (!text) {
                    return "";
                }

                if (showFullContent) {
                    return <MentionInputRender value={text} linkColor={"primary"} />;
                }

                return <TruncatedMentionText value={text} />;
            }

            case EntityFieldType.Number:
            case EntityFieldType.Decimal: {
                if (isNullOrWhitespace(field.value.toString())) {
                    return "";
                }

                let number = translateNumber(Number(field.value));
                if (field.column && field.column == ContractColumn.ExpiryNotificationDays) {
                    number = `${number} ${translateString("days")}`;
                }

                return number;
            }

            case EntityFieldType.Boolean:
                if (field.value === null || field.value === undefined) return "";
                return field.value ? translateString("yes") : field.value === "" ? "" : translateString("no");

            // False draws nothing rather than an unticked box: these columns mark the few rows that are approved
            // or preselected, and a column of empty boxes reads as noise next to the ticks that carry the meaning.
            case EntityFieldType.BooleanCheckmark:
                return field.value ? <CheckIcon sx={{ height: "20px !important" }} /> : "";

            case EntityFieldType.Date:
            case EntityFieldType.DateTime:
                return field.value ? translateDate(new Date(field.value)) : "";

            case EntityFieldType.Timestamp:
                return field.value ? translateDateWithTime(new Date(field.value)) : "";

            case EntityFieldType.DateWithOptionalTime:
                return field.value ? renderDateWithOptionalTime(field.value) : "";

            case EntityFieldType.Status: {
                const status = field.value as ContractStatusWithOverdue;
                const statusValue = translateContractStatusEnum(status.status.toString());

                return (
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <ContractStatusChip value={statusValue} status={status.status} />
                        {status.isOverdue === true ? <ContractStatusChip value={translateString("expired")} status={"Expired"} /> : undefined}
                    </Box>
                );
            }

            case EntityFieldType.LegalEntityStatus:
                return <LegalEntityStatusChip isActive={field.value as boolean} />;

            case EntityFieldType.IncidentLogStatus:
                return <IncidentLogStatusChip status={field.value as IncidentLogEntryStatus} />;

            case EntityFieldType.IncidentLogSeverity:
                return <IncidentLogRisk severityLevel={field.value as SeverityLevelEnum} />;

            case EntityFieldType.ProcessingActivityStatus:
                return <ProcessingActivityStatusChip status={field.value as ProcessingActivityStatus} />;

            case EntityFieldType.ProcessingActivityProgress: {
                const progress = field.value as ProcessingActivityProgressValue;
                // The bar cracks when given a hover text, which is how the overview marks a validation as due.
                return (
                    <DotLegalCompletedPercentage
                        completed={progress.progress}
                        hoverText={progress.isValidationDue ? translateString("processingValidationDue") : undefined}
                    />
                );
            }

            case EntityFieldType.BusinessAreaList:
            case EntityFieldType.TagList: {
                const chips = (field.value as NameWithColor[] | null) ?? [];
                if (chips.length === 0) {
                    return "";
                }

                return showFullContent ? <ColoredChips items={chips} /> : <ColoredChipsCell items={chips} />;
            }

            case EntityFieldType.EntityList: {
                const entities = field.value as string[];
                let list = entities.map((x) => {
                    return { id: "", name: x };
                });
                if (!entities || entities.length === 0) return "";

                if (showFullContent) {
                    return (
                        <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                            {list?.map((x) => x.name).join("\n")}
                        </Box>
                    );
                }

                return <LegalEntitiesColumn legalEntities={list} />;
            }

            case EntityFieldType.CounterPartyWithSubContractors: {
                interface CounterPartyWithSubContractorsTest {
                    name: string;
                }
                interface CounterPartyWithSubContractors {
                    name: string;
                    subContractors: Array<CounterPartyWithSubContractorsTest>;
                }
                const entities = field.value as Array<CounterPartyWithSubContractors>;

                if (!entities || entities.length === 0) return "";

                return (
                    <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                        {entities?.map((x, i) => {
                            let test =
                                x.subContractors.length > 0 ? `(${translateString("subContractors")}: ${x.subContractors.map((y) => y.name).join(", ")})` : "";
                            return (
                                <Box key={i}>
                                    {x.name} {test}
                                </Box>
                            );
                        })}
                    </Box>
                );
            }

            case EntityFieldType.ComplianceAreaList: {
                const complianceAreas = field.value as string[];
                if (!complianceAreas || complianceAreas.length === 0) {
                    return "";
                }

                return (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {complianceAreas.map((complianceArea) => (
                            <ComplianceAreaWrapper key={complianceArea} complianceArea={complianceArea} />
                        ))}
                    </Box>
                );
            }

            case EntityFieldType.Amount: {
                // Picking a currency before entering an amount saves the currency alone, which is not an amount to show.
                const amountField = field.value as AmountWithCurrency | null;
                if (amountField?.amount === null || amountField?.amount === undefined) {
                    return "";
                }

                return `${translateNumber(amountField.amount)} ${amountField.currencyCode}`;
            }

            case EntityFieldType.RenewalCountdown: {
                const countdown = field.value as RenewalCountdownValue;

                const tooltipDate = countdown.endDate ? translateDate(new Date(countdown.endDate)) : "";
                const isDays = countdown.daysRemaining !== undefined && countdown.daysRemaining !== null;
                const isRange = !isDays && countdown.range !== undefined && countdown.range !== null;

                let displayText: string;
                if (isDays) {
                    displayText = translateString("inXDays", { count: countdown.daysRemaining });
                } else if (isRange) {
                    switch (countdown.range) {
                        case RenewalCountdownRange.WithinAMonth:
                            displayText = translateString("inAMonth");
                            break;
                        case RenewalCountdownRange.Within2Months:
                            displayText = translateString("in2Months");
                            break;
                        case RenewalCountdownRange.Within3Months:
                            displayText = translateString("in3Months");
                            break;
                        default:
                            displayText = tooltipDate;
                    }
                } else {
                    displayText = tooltipDate;
                }

                if (isDays || isRange) {
                    return (
                        <DotLegalTooltip text={tooltipDate}>
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    borderRadius: 10,
                                    paddingX: 1,
                                    paddingY: 0.5,
                                    fontSize: "0.8125rem",
                                    backgroundColor: isDays ? "#FDECEA" : "#FFF8E1",
                                }}
                            >
                                {displayText}
                            </Box>
                        </DotLegalTooltip>
                    );
                }

                return <span>{displayText}</span>;
            }

            case EntityFieldType.Template: {
                const template = field.value as ContractTemplateInfo;
                return template.templateName;
            }

            case EntityFieldType.MultiLineText: {
                const text = field.value as string;

                if (!text) {
                    return "";
                }

                if (showFullContent) {
                    return <Box sx={{ whiteSpace: "pre-line" }}>{text}</Box>;
                }

                return (
                    <DotLegalTooltip text={text}>
                        <Box sx={{ ...multiLineTextStyles }}>{text}</Box>
                    </DotLegalTooltip>
                );
            }

            case EntityFieldType.AutoRenewal: {
                const autoRenewal = field.value as AutoRenewalValue;
                if (!autoRenewal?.duration || !autoRenewal?.durationType) {
                    return "";
                }

                const durationTypeLabel =
                    {
                        [AutoRenewalDurationType.Months]: translateString("months"),
                        [AutoRenewalDurationType.Years]: translateString("years"),
                    }[autoRenewal.durationType] ?? "";

                return `${autoRenewal.duration} ${durationTypeLabel}`;
            }

            case EntityFieldType.ProcessStatus:
                return <ProcessStatusChip status={field.value as ProcessStatus} />;

            case EntityFieldType.SystemStatus: {
                const status = field.value as SystemStatus;
                const statusTranslationKeys: Record<SystemStatus, string> = {
                    [SystemStatus.Active]: "active",
                    [SystemStatus.UnderImplementation]: "underImplementation",
                    [SystemStatus.PhasedOut]: "phasedOut",
                    [SystemStatus.UnderRetirement]: "underRetirement",
                };

                const label = translateString(statusTranslationKeys[status]);
                return (
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <SystemStatusChip value={label} status={status} />
                    </Box>
                );
            }

            case EntityFieldType.SystemStorageType: {
                const storageType = field.value as SystemStorageType;
                switch (storageType) {
                    case SystemStorageType.OnPremise:
                        return translateString("onPremise");
                    case SystemStorageType.Hybrid:
                        return translateString("hybrid");
                    case SystemStorageType.Cloud:
                        return translateString("cloud");
                    default:
                        exhaustiveGuard(storageType);
                }
                break;
            }

            case EntityFieldType.SystemLevelOfCriticality: {
                const level = field.value as OperationCriticalityLevelEnum;
                switch (level) {
                    case OperationCriticalityLevelEnum.VeryLow:
                        return translateOperationCriticalityLevelEnum(OperationCriticalityLevelEnum.VeryLow);
                    case OperationCriticalityLevelEnum.Low:
                        return translateOperationCriticalityLevelEnum(OperationCriticalityLevelEnum.Low);
                    case OperationCriticalityLevelEnum.Moderate:
                        return translateOperationCriticalityLevelEnum(OperationCriticalityLevelEnum.Moderate);
                    case OperationCriticalityLevelEnum.High:
                        return translateOperationCriticalityLevelEnum(OperationCriticalityLevelEnum.High);
                    case OperationCriticalityLevelEnum.VeryHigh:
                        return translateOperationCriticalityLevelEnum(OperationCriticalityLevelEnum.VeryHigh);

                    default:
                        exhaustiveGuard(level);
                }
                break;
            }

            case EntityFieldType.SystemDataTypes: {
                const dataTypes = field.value as Array<string>;
                const translatedDataTypes = dataTypes.map((x) => translateString(setFirstLetterToLowerCase(SystemDataType[Number(x)])));
                return translatedDataTypes.join(", ");
            }

            case EntityFieldType.Countries: {
                const countryCodes = field.value as Array<string>;
                const translatedDataTypes = countryCodes.map((x) => translateCountry(x));
                return translatedDataTypes.join(", ");
            }

            case EntityFieldType.SystemType: {
                const distributionArea = field.value as DistributionArea;
                switch (distributionArea) {
                    case DistributionArea.GlobalSystem:
                        return translateString("globalSystem");
                    case DistributionArea.LocalSystem:
                        return translateString("localsystem");
                    case DistributionArea.PhysicalArchive:
                        return translateString("physicalArchive");
                    default:
                        exhaustiveGuard(distributionArea);
                }
                break;
            }

            case EntityFieldType.Evaluation: {
                const evaluation = field.value as EvaluationValue | null;
                return <QuestionnaireEvaluation evaluationStatus={evaluation?.evaluationStatus} evaluation={evaluation?.evaluation} />;
            }

            case EntityFieldType.SystemEntityType: {
                const systemType = field.value as SystemEntityType;
                return translateString(setFirstLetterToLowerCase(SystemEntityType[systemType].toString()));
            }

            default:
                return field.value?.toString() || "";
        }
    }
    return renderFieldValue();
}

export default DynamicCellValueRenderer;
